import { useEffect, lazy, Suspense } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchRouteById } from '../lib/api';
import { RouteHero } from '../components/RouteHero';
import { trackRouteView } from '../lib/analytics';

// Lazy load RouteMap to reduce initial bundle size (Leaflet is ~150KB)
const RouteMap = lazy(() => import('../components/RouteMap').then(m => ({ default: m.RouteMap })));

/**
 * Route Detail Page (Phase 2.1)
 *
 * Individual route page with full details and "Plan your kit" CTA.
 * Optimized for SEO with proper meta tags.
 */

function formatCountry(country: string): string {
  return country.charAt(0).toUpperCase() + country.slice(1);
}

function formatConsideration(consideration: string): string {
  return consideration
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function formatMonth(month: string): string {
  return month.charAt(0).toUpperCase() + month.slice(1);
}

export function RoutePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['route', id],
    queryFn: () => fetchRouteById(id!),
    enabled: !!id,
  });

  // Update document title, meta tags, and structured data for SEO
  useEffect(() => {
    if (data?.route) {
      const route = data.route;
      document.title = `${route.name} - Gear Recommendations | NFA`;
      trackRouteView(route.name, route.country);

      // OG Meta Tags
      const ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      ogTitle.setAttribute('content', `${route.name} - Gear Recommendations | NFA`);
      document.head.appendChild(ogTitle);

      const ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      const descText = `${route.distance_km}km route through ${formatCountry(route.country)}. ${route.typical_days_min}-${route.typical_days_max} days typical duration.`;
      ogDescription.setAttribute('content', descText);
      document.head.appendChild(ogDescription);

      const ogImage = document.createElement('meta');
      ogImage.setAttribute('property', 'og:image');
      const apiBaseUrl = import.meta.env.VITE_API_URL || 'https://api.notfuckingabout.com';
      ogImage.setAttribute('content', `${apiBaseUrl}/api/og/route/${route.id}`);
      document.head.appendChild(ogImage);

      const ogType = document.createElement('meta');
      ogType.setAttribute('property', 'og:type');
      ogType.setAttribute('content', 'website');
      document.head.appendChild(ogType);

      // Twitter Card Meta Tags
      const twitterCard = document.createElement('meta');
      twitterCard.setAttribute('name', 'twitter:card');
      twitterCard.setAttribute('content', 'summary_large_image');
      document.head.appendChild(twitterCard);

      // JSON-LD Structured Data
      const jsonLd = document.createElement('script');
      jsonLd.setAttribute('type', 'application/ld+json');
      const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'TouristDestination',
        name: route.name,
        description: `${route.distance_km}km hiking route through ${formatCountry(route.country)}. ${route.typical_days_min}-${route.typical_days_max} days typical duration. Terrain: ${route.terrain}.`,
        touristType: 'Hiking',
        geo: route.center_lat && route.center_lng ? {
          '@type': 'GeoCoordinates',
          latitude: route.center_lat,
          longitude: route.center_lng,
        } : undefined,
      };
      jsonLd.textContent = JSON.stringify(structuredData);
      document.head.appendChild(jsonLd);

      return () => {
        document.title = 'NFA - Gear Recommendations';
        ogTitle.remove();
        ogDescription.remove();
        ogImage.remove();
        ogType.remove();
        twitterCard.remove();
        jsonLd.remove();
      };
    }
    return () => {
      document.title = 'NFA - Gear Recommendations';
    };
  }, [data]);

  const handlePlanKit = () => {
    if (data?.kit_planning_hint) {
      // Navigate to home with prefilled query
      navigate('/', { state: { prefillQuery: data.kit_planning_hint } });
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6">
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6">
        <Link
          to="/routes"
          className="inline-flex items-center gap-2 text-neutral-400 hover:text-white text-sm mb-8 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to routes
        </Link>

        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-8 text-center">
          <h2 className="font-mono text-xl font-bold text-red-400 mb-2">Route Not Found</h2>
          <p className="text-neutral-400">
            The route you're looking for doesn't exist or has been removed.
          </p>
          <Link
            to="/routes"
            className="inline-block mt-4 px-4 py-2 bg-orange-500 text-black font-medium rounded-lg hover:bg-orange-400 transition-colors"
          >
            Browse all routes
          </Link>
        </div>
      </div>
    );
  }

  const { route, kit_planning_hint } = data;

  const durationText = route.typical_days_min === route.typical_days_max
    ? `${route.typical_days_min} days`
    : `${route.typical_days_min}-${route.typical_days_max} days`;

  return (
    <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6">
      <Link
        to="/routes"
        className="inline-flex items-center gap-2 text-neutral-400 hover:text-white text-sm mb-6 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        All routes
      </Link>

      {/* Hero Image */}
      <RouteHero
        imageUrl={route.image_url}
        attribution={route.image_attribution}
        routeName={route.name}
      />

      {/* Header */}
      <div className="mt-8 mb-8">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-sm text-neutral-500 uppercase tracking-wider">
            {formatCountry(route.country)}
            {route.region && ` · ${route.region}`}
          </span>
          <span className={`px-2 py-0.5 text-xs rounded ${
            route.terrain === 'technical'
              ? 'bg-red-500/10 text-red-400'
              : route.terrain === 'moderate'
              ? 'bg-yellow-500/10 text-yellow-400'
              : 'bg-green-500/10 text-green-400'
          }`}>
            {route.terrain.charAt(0).toUpperCase() + route.terrain.slice(1)}
          </span>
        </div>
        <h1 className="font-mono text-3xl sm:text-4xl font-bold text-white mb-4">
          {route.name}
        </h1>
      </div>

      {/* Route Map (Phase 5.2: prefer simplified geometry for performance) */}
      {(route.geometry_simplified || route.gpx_data || route.center_lat) && (
        <div className="mb-8">
          <Suspense fallback={
            <div className="h-64 sm:h-80 rounded-lg bg-neutral-800 animate-pulse flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-orange-500" />
            </div>
          }>
            <RouteMap
              geometrySimplified={route.geometry_simplified}
              gpxData={route.gpx_data}
              centerLat={route.center_lat}
              centerLng={route.center_lng}
              routeName={route.name}
            />
          </Suspense>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-4">
          <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Distance</p>
          <p className="font-mono text-2xl font-bold text-white">{route.distance_km}km</p>
        </div>
        <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-4">
          <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Duration</p>
          <p className="font-mono text-2xl font-bold text-white">{durationText}</p>
        </div>
        {route.elevation_gain_m && (
          <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-4">
            <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Total Ascent</p>
            <p className="font-mono text-2xl font-bold text-white">{route.elevation_gain_m.toLocaleString()}m</p>
          </div>
        )}
        {route.highest_point_m && (
          <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-4">
            <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">High Point</p>
            <p className="font-mono text-2xl font-bold text-white">{route.highest_point_m.toLocaleString()}m</p>
          </div>
        )}
      </div>

      {/* Route Details */}
      <div className="grid sm:grid-cols-2 gap-6 mb-8">
        {/* Water & Resupply */}
        <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-5">
          <h2 className="font-mono text-lg font-semibold text-white mb-4">Logistics</h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-neutral-400">Water Availability</span>
              <span className={`font-medium ${
                route.water_availability === 'scarce' ? 'text-red-400' :
                route.water_availability === 'moderate' ? 'text-yellow-400' : 'text-green-400'
              }`}>
                {route.water_availability.charAt(0).toUpperCase() + route.water_availability.slice(1)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-400">Resupply Options</span>
              <span className={`font-medium ${
                route.resupply_options === 'minimal' ? 'text-red-400' :
                route.resupply_options === 'moderate' ? 'text-yellow-400' : 'text-green-400'
              }`}>
                {route.resupply_options.charAt(0).toUpperCase() + route.resupply_options.slice(1)}
              </span>
            </div>
          </div>
        </div>

        {/* Best Months */}
        <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-5">
          <h2 className="font-mono text-lg font-semibold text-white mb-4">Best Time to Go</h2>
          <div className="flex flex-wrap gap-2">
            {route.best_months.map((month) => (
              <span
                key={month}
                className="px-3 py-1 bg-orange-500/10 text-orange-400 text-sm rounded-full"
              >
                {formatMonth(month)}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Special Considerations */}
      {route.special_considerations.length > 0 && (
        <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-5 mb-8">
          <h2 className="font-mono text-lg font-semibold text-white mb-4">Things to Know</h2>
          <ul className="space-y-2">
            {route.special_considerations.map((consideration) => (
              <li key={consideration} className="flex items-start gap-2 text-neutral-400">
                <svg className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{formatConsideration(consideration)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* CTA */}
      <div className="bg-gradient-to-r from-orange-500/10 to-transparent border border-orange-500/20 rounded-lg p-6 sm:p-8">
        <h2 className="font-mono text-xl sm:text-2xl font-bold text-white mb-3">
          Ready to pack for {route.name}?
        </h2>
        <p className="text-neutral-400 mb-6">
          Get a complete gear list tailored to this route's terrain, weather patterns, and specific challenges.
        </p>
        <button
          onClick={handlePlanKit}
          className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 text-black font-bold rounded-lg hover:bg-orange-400 transition-colors"
        >
          <span>Plan Your Kit</span>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </button>
        <p className="text-sm text-neutral-500 mt-4">
          Suggested search: <span className="text-neutral-300">{kit_planning_hint}</span>
        </p>
      </div>

      {/* SEO Content */}
      <div className="mt-16 pt-8 border-t border-neutral-800 prose prose-invert prose-sm max-w-none">
        <h2 className="font-mono text-xl font-bold text-white mb-4">
          What Gear Do You Need for {route.name}?
        </h2>
        <p className="text-neutral-400">
          The {route.name} covers {route.distance_km}km through {formatCountry(route.country)}'s
          {route.terrain === 'technical' ? ' challenging' : route.terrain === 'moderate' ? ' varied' : ' accessible'} terrain.
          {route.water_availability === 'scarce' && ' Water sources are scarce, so plan to carry extra capacity.'}
          {route.resupply_options === 'minimal' && ' Resupply options are limited, requiring you to carry more food.'}
        </p>
        <p className="text-neutral-400 mt-4">
          The best time to tackle this route is {route.best_months.slice(0, -1).map(formatMonth).join(', ')}
          {route.best_months.length > 1 ? ` or ${formatMonth(route.best_months[route.best_months.length - 1])}` : formatMonth(route.best_months[0])}.
        </p>
      </div>
    </div>
  );
}
